import { AdornisRouter } from '@adornis/base/client/a-router';
import { Maybe } from '@adornis/base/utilTypes';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { acss } from '@adornis/chemistry/directives/acss';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/x-card';
import '@adornis/chemistry/elements/components/x-flex';
import '@adornis/chemistry/elements/components/x-icon';
import '@adornis/chemistry/elements/components/x-link';
import { ElementsLink } from '@adornis/chemistry/elements/components/x-link';
import '@adornis/file-utils/client/x-image';
import '@adornis/germanium/components/ge-button';
import '@adornis/germanium/components/ge-section';
import { geSpacing } from '@adornis/germanium/constants';
import '@adornis/popover/a-drop-wrapper';
import { ADropTrigger } from '@adornis/popover/a-drop-wrapper';
import { AdornisUser } from '@adornis/users/db/a-user';
import logoSVG from 'client/assets/svgs/brand_welcome.svg';
import { config } from 'config';
import { WTGUser } from 'db/wtg-user';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { Observable, startWith, tap } from 'rxjs';

export type MenuItem = { name: string; link?: string; menuItems?: MenuItem[]; guard?: () => boolean };

@customElement('wtg-navbar')
export class WTGNavbar extends ChemistryLitElement {
  styles = () => ({
    ':host': {
      boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.05)',
    },
    '[active]': { color: this.colors.accent },
  });

  size = new SizeController(this);

  @property({ reflect: true, type: Boolean }) showMenu = false;
  @state() page = new ObservableController(this, AdornisRouter.get('page').pipe(tap(() => (this.showMenu = false))));

  @state() user = new ObservableController(
    this,
    WTGUser.currentUser.pipe(startWith(undefined)) as Observable<Maybe<WTGUser>>,
  );

  @state() isTop = true;

  private menuItems: MenuItem[] = [
    { name: 'about us', link: '/' },
    { name: 'blog', link: '/blog' },
    {
      name: 'community',
      menuItems: [
        { name: 'forum', link: config.get('NODEBB_DOMAIN') },
        { name: 'events', link: '/events' },
        { name: 'groups', link: config.get('NODEBB_DOMAIN') },
        // { name: 'mentorship', link: '/404' },
      ],
    },
    { name: 'welcome box', link: '/welcome_box', guard: () => !!this.user.value },
    // { name: 'partner services', link: '/404' },
    {
      name: 'more',
      menuItems: [
        { name: 'talks', link: 'https://youtube.com/@welcometogermany4660' },
        { name: 'for entrepreneurs', link: 'https://www.internationalfoundersaward.com/' },
        { name: 'FAQs', link: '/faq' },
        { name: 'contact', link: '/404' },
      ],
    },
  ];

  public render() {
    if (this.size.isTablet) return this.loadMobile(this.showMenu, window.innerWidth, this.isTop, this.user.value);

    const renderMenuItem = (menuItem: MenuItem, level: number = 0) => html`
      <x-link
        slot="trigger"
        href=${ifDefined(menuItem.link)}
        target=${menuItem.link === config.get('NODEBB_DOMAIN') ? '_self' : ''}
        @click=${(e: Event) => (e.currentTarget as ElementsLink).querySelector<ADropTrigger>('a-dropdown')?.toggle()}
        ${css({ padding: this.spacing.md, display: 'flex', alignItems: 'center' })}
      >
        <a-dropdown>
          <x-flex crossaxis-center horizontal space="sm">
            ${level > 0 ? html` <x-icon ${css({ color: this.colors.accent })}> arrow_right_alt </x-icon> ` : nothing}
            <ge-text ?active=${this.page.value === menuItem.link?.split('/')[1]}>
              ${menuItem.name.toUpperCase()}
            </ge-text>
            ${menuItem.menuItems?.length ? html` <x-icon> keyboard_arrow_down </x-icon> ` : nothing}
          </x-flex>

          <ge-card
            ${css({ padding: '0' })}
            slot="dropdown-content"
            @click=${(e: Event) =>
              this.shadowRoot?.querySelectorAll<ADropTrigger>('a-dropdown').forEach(drop => drop.close())}
          >
            <x-flex> ${menuItem.menuItems?.map(item => renderMenuItem(item, level + 1))} </x-flex>
          </ge-card>
        </a-dropdown>
      </x-link>
    `;

    return html`
      <x-flex ${css({ width: '100%', padding: `${geSpacing.sm} ${this.spacing.lg}`, backgroundColor: 'white' })}>
        <x-flex horizontal crossaxis-center>
          <x-flex horizontal flex crossaxis-center space="md">
            <x-link href="/">
              <x-image is-url mode="width" .value=${logoSVG} ${css({ width: '152px' })}></x-image>
            </x-link>

            ${this.showNavbarItems()
              ? html`
                  <x-flex horizontal ${acss({ height: '100%' })}>
                    ${this.menuItems.map(menuItem => (menuItem.guard?.() || true ? renderMenuItem(menuItem) : nothing))}
                  </x-flex>
                `
              : html`<div ${css({ height: '56px' })}></div>`}
          </x-flex>

          <x-flex horizontal space="md">
            ${this.user.value
              ? html`
                  <x-flex
                    horizontal
                    space="md"
                    padding="sm"
                    crossaxis-center
                    center
                    ${css({ backgroundColor: this.isTop ? 'rgba(255, 255, 255, 0.4)' : '', borderRadius: '5px' })}
                  >
                    <ge-text primary> ${this.user.value.displayName} </ge-text>
                    <ge-button
                      @click=${() => WTGUser.logout().then(() => AdornisRouter.goTo('/'))}
                      mode="icon"
                      icon="logout"
                    >
                    </ge-button>
                  </x-flex>
                `
              : this.showNavbarItems()
              ? html`
                  <ge-button href="/signup" mode="action" iconend icon="arrow_forward"> SIGN UP </ge-button>
                  <ge-button href="/login" mode="outline" tone="error"> LOGIN </ge-button>
                `
              : nothing}
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private showNavbarItems() {
    return !(
      this.page.value === 'login' ||
      this.page.value === 'signup' ||
      this.page.value === 'create_profile' ||
      this.page.value === 'welcome_box'
    );
  }

  public loadMobile(showMenu: boolean, width: number, isTop: boolean, user: AdornisUser | null | undefined) {
    return html`
      <style>
        @keyframes fadeIn {
          0% {
            opacity: 0;
            top: 0;
          }

          100% {
            opacity: 1;
            top: 0;
          }
        }

        @keyframes fadeOut {
          0% {
            opacity: 1;
            top: 0;
          }

          99% {
            opacity: 0;
            top: 0;
          }

          100% {
            opacity: 0;
            top: -100vh;
          }
        }
      </style>

      <x-flex
        horizontal
        space-between
        crossaxis-center
        ${css({
          width: '100%',
          height: 'auto',
          padding: `${this.spacing.lg} ${width < 490 ? this.spacing.md : this.spacing.lg}`,
          transition: 'all 300ms ease',
          backgroundColor: isTop ? '' : 'rgba(255, 255, 255, 0.7)',
        })}
      >
        <x-flex horizontal space="md" crossaxis-center @click=${() => AdornisRouter.goTo('/')}>
          <x-image is-url .value=${logoSVG} ${css({ width: '152px', height: '36px' })}></x-image>
        </x-flex>

        <x-flex @click=${() => (this.showMenu = !showMenu)}>
          <x-icon ${css({ fontSize: '39px', color: this.colors.secondary, display: showMenu ? 'none' : 'flex' })}>
            menu
          </x-icon>
        </x-flex>
      </x-flex>

      <x-flex
        padding="md"
        ${css({
          width: '100%',
          height: '100vh',
          position: 'fixed',
          backgroundColor: this.colors.neutral_300,
          borderBottomLeftRadius: '30%',
          animationName: showMenu ? 'fadeIn' : 'fadeOut',
          animationDuration: '.3s',
          animationFillMode: 'both',
          zIndex: '100',
        })}
      >
        ${this.mobileItems()}
      </x-flex>
    `;
  }

  @state() mobileMenuItemOpened: null | MenuItem = null;
  private mobileItems() {
    const renderMobileMenuItem = (menuItem: MenuItem) => html`
      <x-link
        href=${ifDefined(menuItem.link)}
        @click=${() => (menuItem.menuItems?.length ? (this.mobileMenuItemOpened = menuItem) : '')}
      >
        <x-flex space="xs" padding="md">
          <x-flex horizontal space="md">
            <ge-h4> ${menuItem.name} </ge-h4> ${menuItem.menuItems?.length
              ? html` <x-icon> keyboard_arrow_down </x-icon> `
              : nothing}
          </x-flex>
          <div ${css({ width: '30px', backgroundColor: this.colors.secondary, height: '2px' })}></div>
          ${this.mobileMenuItemOpened === menuItem
            ? html` <x-flex> ${menuItem.menuItems?.map(item => renderMobileMenuItem(item))} </x-flex>`
            : nothing}
        </x-flex>
      </x-link>
    `;

    return html`
      <x-flex space="md">
        <x-flex horizontal space-between ${css({ padding: '14px 0px' })}>
          <x-flex horizontal crossaxis-center @click=${() => AdornisRouter.goTo('/')}>
            <x-image is-url .value=${logoSVG} ${css({ width: '152px', height: '36px' })}></x-image>
          </x-flex>

          <x-icon
            ${css({ fontSize: '36px', color: 'red', display: this.showMenu ? 'flex' : 'none' })}
            @click=${() => (this.showMenu = false)}
          >
            close
          </x-icon>
        </x-flex>

        <x-flex>
          ${this.menuItems.map(menuItem => renderMobileMenuItem(menuItem))}

          <x-flex center crossaxis-center ${css({ marginTop: '50px' })}>
            <x-flex horizontal space="md">
              ${this.user.value
                ? html`
                    <x-flex space="md" crossaxis-center center>
                      <ge-text primary> ${this.user.value.username} </ge-text>
                      <ge-button @click=${() => WTGUser.logout().then(() => AdornisRouter.goTo('/'))} mode="outline">
                        LOGOUT
                      </ge-button>
                    </x-flex>
                  `
                : html`
                    <ge-button href="/login" mode="outline"> LOGIN </ge-button>
                    <ge-button href="/signup" mode="outline"> SIGN UP </ge-button>
                  `}
            </x-flex>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }
}
